/**
 * 🗺️ YACHT DESTINATIONS SERVICE
 * Handles yacht destination-based pricing catalog management
 * Admin endpoints for managing countries, cities, and destinations
 */

import apiClient from './apiClient';

export interface YachtDestination {
  id: string;
  country: string;
  city: string;
  destinationName: string;
  description?: string;
  address?: string;
  location: {
    latitude?: number;
    longitude?: number;
    estimatedDistanceKm?: number;
  };
  isActive: boolean;
  yachtCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Country {
  country: string;
  cityCount: number;
  destinationCount: number;
  activeDestinationCount: number;
}

export interface City {
  city: string;
  destinationCount: number;
  activeDestinationCount: number;
}

export const yachtDestinationsService = {
  /**
   * 🌍 List All Countries
   * GET /api/v1/admin/yacht-destinations/countries
   */
  async getCountries(): Promise<{
    success: boolean;
    data?: { countries: Country[]; total: number };
    error?: string;
  }> {
    try {
      const response = await apiClient.get('/api/v1/admin/yacht-destinations/countries');

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch countries',
      };
    } catch (error: any) {
      console.error('❌ Error fetching countries:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch countries',
      };
    }
  },

  /**
   * 🏙️ List Cities in Country
   * GET /api/v1/admin/yacht-destinations/cities?country=Nigeria
   */
  async getCities(country: string): Promise<{
    success: boolean;
    data?: { country: string; cities: City[]; total: number };
    error?: string;
  }> {
    try {
      const response = await apiClient.get('/api/v1/admin/yacht-destinations/cities', {
        params: { country },
      });

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch cities',
      };
    } catch (error: any) {
      console.error('❌ Error fetching cities:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch cities',
      };
    }
  },

  /**
   * 📍 List/Filter Destinations
   * GET /api/v1/admin/yacht-destinations?country=Nigeria&city=Lagos&active=true
   */
  async getDestinations(filters?: {
    country?: string;
    city?: string;
    active?: boolean;
  }): Promise<{
    success: boolean;
    data?: { destinations: YachtDestination[]; total: number };
    error?: string;
  }> {
    try {
      const response = await apiClient.get('/api/v1/admin/yacht-destinations', {
        params: filters,
      });

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch destinations',
      };
    } catch (error: any) {
      console.error('❌ Error fetching destinations:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch destinations',
      };
    }
  },

  /**
   * ➕ Create New Destination
   * POST /api/v1/admin/yacht-destinations
   */
  async createDestination(data: {
    country: string;
    city: string;
    destinationName: string;
    description?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    estimatedDistanceKm?: number;
  }): Promise<{
    success: boolean;
    data?: YachtDestination;
    error?: string;
  }> {
    try {
      const response = await apiClient.post('/api/v1/admin/yacht-destinations', data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to create destination',
      };
    } catch (error: any) {
      console.error('❌ Error creating destination:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to create destination',
      };
    }
  },

  /**
   * ✏️ Update Destination
   * PUT /api/v1/admin/yacht-destinations/{destinationId}
   */
  async updateDestination(
    destinationId: string,
    data: {
      destinationName?: string;
      description?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      estimatedDistanceKm?: number;
      isActive?: boolean;
    }
  ): Promise<{
    success: boolean;
    data?: YachtDestination;
    error?: string;
  }> {
    try {
      const response = await apiClient.put(
        `/api/v1/admin/yacht-destinations/${destinationId}`,
        data
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to update destination',
      };
    } catch (error: any) {
      console.error('❌ Error updating destination:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update destination',
      };
    }
  },

  /**
   * 🔄 Toggle Destination Status
   * PATCH /api/v1/admin/yacht-destinations/{destinationId}
   */
  async toggleDestinationStatus(destinationId: string): Promise<{
    success: boolean;
    message?: string;
    data?: { id: string; destinationName: string; isActive: boolean };
    error?: string;
  }> {
    try {
      const response = await apiClient.patch(
        `/api/v1/admin/yacht-destinations/${destinationId}`,
        {} // Empty body - endpoint automatically toggles status
      );

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to toggle destination status',
      };
    } catch (error: any) {
      console.error('❌ Error toggling destination status:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to toggle destination status',
      };
    }
  },

  /**
   * 🗑️ Delete Destination
   * DELETE /api/v1/admin/yacht-destinations/{destinationId}
   */
  async deleteDestination(destinationId: string): Promise<{
    success: boolean;
    data?: { id: string; destinationName: string };
    error?: string;
    activeYachtCount?: number;
  }> {
    try {
      const response = await apiClient.delete(
        `/api/v1/admin/yacht-destinations/${destinationId}`
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to delete destination',
        activeYachtCount: response.data.activeYachtCount,
      };
    } catch (error: any) {
      console.error('❌ Error deleting destination:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete destination',
        activeYachtCount: error.response?.data?.activeYachtCount,
      };
    }
  },
};

export default yachtDestinationsService;
